import { api } from './api';

export type BillingPlanTier = 'STARTER' | 'BASIC' | 'PRO';

export interface BillingPlanPromoCode {
  promoCode: string;
  promoName?: string | null;
  defaultPromo?: boolean;
}

export interface BillingPricePlan {
  productCode: BillingPlanTier;
  productType: string;
  displayName: string;
  currency: string;
  normalPriceMinor: number;
  effectivePriceMinor: number;
  billingPeriod: string;
  displayOrder: number;
  active: boolean;
  visibleInCustomerPortal: boolean;
  customerSelectable: boolean;
  defaultPromoCode?: string | null;
  availablePromoCodes?: BillingPlanPromoCode[];
}

export interface PaymentCheckoutSessionRequest {
  targetPlan: BillingPlanTier;
  promoCode?: string;
}

export interface PaymentCheckoutSessionResponse {
  checkoutSessionToken: string;
  status: string;
  expiresAt: string;
  nextBillingDate?: string;
  provider: 'PAYPAL';
  providerRedirectUrl: string;
  paymentOnlineUrl?: string;
  paymentType: string;
  billingInterval?: string;
  pricingMode: string;
  amountMinor: number;
  originalAmountMinor?: number;
  promotionAmountMinor?: number;
  paidAmountMinor?: number;
  currency: string;
  promotionType?: string;
  promoCode?: string;
  promotionName?: string;
  discountType?: string;
  discountValue?: number;
  promotionStartAt?: string;
  promotionEndAt?: string;
  flowType: string;
  nextExpectedAmountMinor?: number;
  nextExpectedAmountReason?: string;
}

export interface BillingHistoryItem {
  checkoutSessionToken: string;
  provider: string;
  providerPaymentReference?: string;
  productCode?: string;
  priceCode?: string;
  customerReference?: string;
  accountReference?: string;
  tenantReference?: string;
  externalOrderReference?: string;
  targetPlan?: BillingPlanTier;
  amountMinor: number;
  originalAmountMinor?: number;
  promotionAmountMinor?: number;
  paidAmountMinor?: number;
  promotionName?: string;
  promotionType?: string;
  promoCode?: string;
  currency: string;
  status: string;
  createdAt: string;
  paidAt?: string;
}

export interface BillingCheckoutCompletionResponse {
  checkoutSessionToken: string;
  provider: string;
  providerPaymentReference: string;
  status: string;
  merchantNotificationStatus: string;
  tenantReference?: string;
  targetPlan?: BillingPlanTier;
}

export interface BillingCheckoutCancellationResponse {
  checkoutSessionToken: string;
  status: string;
  provider: string;
  updatedAt: string;
}

export interface BillingSubscriptionResponse {
  organizationId: string;
  currentPlan: BillingPlanTier;
  status: string;
  billingPeriodStart?: string;
  billingPeriodEnd?: string;
  freePeriodStart?: string;
  freePeriodEnd?: string;
  freePeriodExtensionCount?: number;
  lastFreePeriodExtendedAt?: string;
  paymentRequiredAt?: string;
  firstPaymentDueAt?: string;
  pendingPlan?: BillingPlanTier;
  pendingChangeEffectiveAt?: string;
  provider?: string;
  providerPaymentReference?: string;
  checkoutSessionToken?: string;
}

export interface ExtendFreeSubscriptionRequest {
  extensionDays: number;
  reason?: string;
}

interface PaymentPlatformBillingHistoryResponse {
  items: BillingHistoryItem[];
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export const billingService = {
  async getPricePlans(scope: 'customer' | 'admin' = 'customer'): Promise<BillingPricePlan[]> {
    const response = await api.get<ApiResponse<BillingPricePlan[]>>('/billing/price-plans', { params: { scope } });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to load price plans.');
    }
    return response.data.data;
  },

  async createCheckoutSession(request: PaymentCheckoutSessionRequest): Promise<PaymentCheckoutSessionResponse> {
    const response = await api.post<ApiResponse<PaymentCheckoutSessionResponse>>('/billing/checkout-sessions', request);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to start payment.');
    }
    return response.data.data;
  },

  async getBillingHistory(): Promise<BillingHistoryItem[]> {
    const response = await api.get<ApiResponse<PaymentPlatformBillingHistoryResponse>>('/billing/history');
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to load payment history.');
    }
    return response.data.data.items;
  },

  async completeCheckoutSession(providerCheckoutReference: string): Promise<BillingCheckoutCompletionResponse> {
    const response = await api.post<ApiResponse<BillingCheckoutCompletionResponse>>('/billing/checkout-sessions/complete', { providerCheckoutReference });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to complete payment.');
    }
    return response.data.data;
  },

  async cancelCheckoutSession(checkoutSessionToken: string): Promise<BillingCheckoutCancellationResponse> {
    const response = await api.post<ApiResponse<BillingCheckoutCancellationResponse>>('/billing/checkout-sessions/cancel', { checkoutSessionToken });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to cancel checkout session.');
    }
    return response.data.data;
  },

  async getSubscription(): Promise<BillingSubscriptionResponse> {
    const response = await api.get<ApiResponse<BillingSubscriptionResponse>>('/billing/subscription');
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to load billing subscription.');
    }
    return response.data.data;
  },

  async schedulePlanChange(targetPlan: BillingPlanTier): Promise<BillingSubscriptionResponse> {
    const response = await api.post<ApiResponse<BillingSubscriptionResponse>>('/billing/plan-changes', { targetPlan });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to schedule plan change.');
    }
    return response.data.data;
  },

  async extendFreeSubscription(request: ExtendFreeSubscriptionRequest): Promise<BillingSubscriptionResponse> {
    const response = await api.post<ApiResponse<BillingSubscriptionResponse>>('/billing/subscription/free-period/extend', request);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to extend free subscription.');
    }
    return response.data.data;
  },
};